import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNpsAccess } from './useNpsAccess';
import {
  DEV2_DEVICE_TYPE_OPTIONS,
  DEV2_ONX_BUBBLE_ID,
  DEV2_DEFAULT_ACCOUNT_NUMBER,
  DEV2_DEFAULT_OXR_BUBBLE_ID,
  DEV2_DEFAULT_PRODUCT,
  DEV2_SCHEDULE_FIRST_NAME,
  DEV2_SCHEDULE_LAST_NAME,
  defaultDev2DeviceTypeForParent,
  defaultBubbleIdForDeviceType,
  defaultInboundDeviceForProduct,
  dev2TwilioToForDeviceType,
  dev2TestingDateForSchedule,
  DEV2_DEFAULT_SOS_LAT,
  DEV2_DEFAULT_SOS_LNG,
  DEV2_INBOUND_DEVICE_OPTIONS,
  buildDev2InboundMessageBody,
  buildDev2InboundWebhookPayload,
  buildDev2InboundWebhookUrlEncoded,
} from './dev2DeviceTestConfig';
import {
  FP_API_BASE,
  DEV2_DEVICE_TEST_SCHEDULE_PATH,
  DEV2_DEVICE_TEST_SOS_PATH,
  postDev2Schedule,
  postDev2Sos,
} from './dev2DeviceTestApi';

function NavigateToHub() {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/global-search', { replace: true });
  }, [navigate]);
  return null;
}

const DEV2_ACTION_MODE_OPTIONS = [
  { value: 'schedule', label: 'Schedule' },
  { value: 'message', label: 'Send message' },
];

function formatJson(data) {
  if (data === undefined || data === null) return '—';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

const Dev2TestingPage = ({ searchContext }) => {
  const { allowed, checked } = useNpsAccess();
  const prefill = searchContext?.dev2TestPrefill;

  const [accountNumber, setAccountNumber] = useState(DEV2_DEFAULT_ACCOUNT_NUMBER);
  const [deviceType, setDeviceType] = useState(DEV2_DEFAULT_PRODUCT);
  const [bubbleId, setBubbleId] = useState(DEV2_DEFAULT_OXR_BUBBLE_ID);
  const [from, setFrom] = useState('');
  const [latitude, setLatitude] = useState(String(DEV2_DEFAULT_SOS_LAT));
  const [longitude, setLongitude] = useState(String(DEV2_DEFAULT_SOS_LNG));
  const [inboundDevice, setInboundDevice] = useState(
    defaultInboundDeviceForProduct(DEV2_DEFAULT_PRODUCT)
  );
  const [bodyAppend, setBodyAppend] = useState('');
  const [actionMode, setActionMode] = useState('message');

  const [busyKey, setBusyKey] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [copyOk, setCopyOk] = useState(false);
  const [error, setError] = useState(null);

  const twilioTo = useMemo(() => dev2TwilioToForDeviceType(deviceType), [deviceType]);

  useEffect(() => {
    if (!prefill) return;
    if (prefill.accountNumber) setAccountNumber(prefill.accountNumber);
    if (prefill.from) setFrom(prefill.from);
    const inferred = defaultDev2DeviceTypeForParent(prefill.parent);
    setDeviceType(inferred);
    setBubbleId(defaultBubbleIdForDeviceType(inferred));
    setInboundDevice(defaultInboundDeviceForProduct(inferred));
  }, [prefill]);

  const handleDeviceTypeChange = (value) => {
    setDeviceType(value);
    setBubbleId(defaultBubbleIdForDeviceType(value));
    setInboundDevice(defaultInboundDeviceForProduct(value));
  };

  const inboundMessageBody = useMemo(
    () =>
      inboundDevice
        ? buildDev2InboundMessageBody(
            inboundDevice,
            accountNumber,
            latitude,
            longitude,
            bodyAppend
          )
        : '',
    [inboundDevice, accountNumber, latitude, longitude, bodyAppend]
  );

  const inboundWebhookPreview = useMemo(() => {
    if (!from.trim() || !inboundMessageBody) return '';
    return buildDev2InboundWebhookUrlEncoded({
      deviceType,
      from: from.trim(),
      to: twilioTo,
      preset: inboundDevice,
      accountNumber,
      latitude,
      longitude,
      bodyAppend,
    });
  }, [
    deviceType,
    from,
    twilioTo,
    inboundDevice,
    accountNumber,
    latitude,
    longitude,
    bodyAppend,
    inboundMessageBody,
  ]);

  const buildScheduleBody = useCallback(
    () => ({
      accountNumber: accountNumber.trim(),
      bubbleId: bubbleId.trim(),
      testingDate: dev2TestingDateForSchedule(),
      firstName: DEV2_SCHEDULE_FIRST_NAME,
      lastName: DEV2_SCHEDULE_LAST_NAME,
      isActive: 1,
    }),
    [accountNumber, bubbleId]
  );

  const buildInboundApiPayload = useCallback(() => {
    return buildDev2InboundWebhookPayload({
      deviceType,
      from: from.trim(),
      to: twilioTo,
      preset: inboundDevice,
      accountNumber,
      latitude,
      longitude,
      bodyAppend,
    });
  }, [
    deviceType,
    from,
    twilioTo,
    inboundDevice,
    accountNumber,
    latitude,
    longitude,
    bodyAppend,
  ]);

  const runApi = async (key, requestRecord, runner) => {
    setError(null);
    setLastResult(null);
    setCopyOk(false);
    setLastRequest(requestRecord);
    setBusyKey(key);
    try {
      const result = await runner();
      setLastResult(result);
      if (!result.ok) {
        const statusNote = result.status != null ? ` (HTTP ${result.status})` : '';
        setError(`Request failed${statusNote}. See response data below.`);
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setBusyKey(null);
    }
  };

  const requireAccount = () => {
    if (!accountNumber.trim()) {
      setError('Account number is required.');
      return false;
    }
    return true;
  };

  const requireBubble = () => {
    if (!bubbleId.trim()) {
      setError(
        deviceType === 'onx'
          ? 'Device bubbleId is required (onX is usually onX App).'
          : 'Enter the device bubbleId for the OXR hardware you are testing.'
      );
      return false;
    }
    return true;
  };

  const requireTwilio = () => {
    if (!requireAccount()) return false;
    if (!from.trim()) {
      setError('Twilio From (member phone) is required.');
      return false;
    }
    if (!inboundDevice) {
      setError('Choose an inbound device sample.');
      return false;
    }
    return true;
  };

  const makeRequestRecord = (action, path, jsonBody) => ({
    action,
    method: 'POST',
    url: `${FP_API_BASE}${path}`,
    jsonBody,
  });

  const runScheduleDevice = () => {
    if (!requireBubble() || !requireAccount()) return;
    const body = buildScheduleBody();
    runApi(
      'schedule',
      makeRequestRecord('Schedule device', DEV2_DEVICE_TEST_SCHEDULE_PATH, body),
      () => postDev2Schedule(body)
    );
  };

  const runSendInbound = () => {
    if (!requireTwilio()) return;
    const body = buildInboundApiPayload();
    runApi(
      'inbound',
      makeRequestRecord('Send SOS/SMS', DEV2_DEVICE_TEST_SOS_PATH, body),
      () => postDev2Sos(body)
    );
  };

  const lastRequestText = useMemo(() => {
    if (!lastRequest) return '';
    return JSON.stringify(lastRequest, null, 2);
  }, [lastRequest]);

  const handleCopyRequest = async () => {
    if (!lastRequestText) return;
    try {
      await navigator.clipboard.writeText(lastRequestText);
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch {
      setError('Could not copy request to clipboard');
    }
  };

  const buttonBusy = (key) => busyKey === key;

  const runPrimaryAction = () => {
    if (actionMode === 'schedule') runScheduleDevice();
    else runSendInbound();
  };

  const primaryBusy = actionMode === 'schedule' ? buttonBusy('schedule') : buttonBusy('inbound');

  const responseStatus = lastResult?.status ?? lastResult?.httpStatus;

  if (!checked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return <NavigateToHub />;
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', py: 3, px: 2 }}>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h5" component="h1">
            Dev2 device test (Ops)
          </Typography>
          <Chip label="Dev2 only" size="small" color="warning" variant="outlined" />
        </Stack>
        {actionMode === 'message' && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Simulate an inbound SOS/SMS. A single digit in append (<code>1</code>, <code>2</code>, …) sends only
            that digit as the message body (test reply).
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {lastResult && (
          <Alert severity={lastResult.ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {lastResult.ok ? 'Completed.' : 'Request finished with errors.'}
            {responseStatus != null && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                status {responseStatus}
              </Typography>
            )}
          </Alert>
        )}

        {lastRequest && (
          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              mb: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '100%', pr: 1 }}
              >
                <Typography variant="subtitle2">
                  Request payload{lastRequest.action ? ` — ${lastRequest.action}` : ''}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyRequest();
                  }}
                  aria-label="Copy request payload"
                  title="Copy for backend"
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {copyOk && (
                <Typography variant="caption" color="success.main" display="block" sx={{ mb: 0.5 }}>
                  Copied to clipboard
                </Typography>
              )}
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', width: '100%' }}>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    m: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                  }}
                >
                  {lastRequestText}
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        )}

        {lastResult && lastResult.data !== undefined && (
          <Accordion
            defaultExpanded={false}
            disableGutters
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Response data</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Paper variant="outlined" sx={{ p: 1.5, width: '100%' }}>
                <Typography
                  component="pre"
                  variant="caption"
                  sx={{
                    m: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                  }}
                >
                  {formatJson(lastResult.data)}
                </Typography>
              </Paper>
            </AccordionDetails>
          </Accordion>
        )}

        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
              Action
            </Typography>
            <ToggleButtonGroup
              value={actionMode}
              exclusive
              fullWidth
              onChange={(_, value) => {
                if (value) setActionMode(value);
              }}
              aria-label="Dev2 action"
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 2,
                p: 0.5,
                gap: 0.5,
                '& .MuiToggleButtonGroup-grouped': {
                  border: 0,
                  borderRadius: 1.5,
                  flex: 1,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: 'text.secondary',
                  transition: 'background-color 0.2s ease, box-shadow 0.2s ease, color 0.2s ease',
                  '&.Mui-selected': {
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'background.paper' },
                  },
                },
              }}
            >
              {DEV2_ACTION_MODE_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value} aria-label={opt.label}>
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <TextField
            label="Account number"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            fullWidth
            required
          />

          {actionMode === 'schedule' && (
            <>
              <FormControl fullWidth required>
                <InputLabel id="dev2-device-type-label">Product</InputLabel>
                <Select
                  labelId="dev2-device-type-label"
                  label="Product"
                  value={deviceType}
                  onChange={(e) => handleDeviceTypeChange(e.target.value)}
                >
                  {DEV2_DEVICE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Device bubbleId"
                value={bubbleId}
                onChange={(e) => setBubbleId(e.target.value)}
                fullWidth
                required
                placeholder={deviceType === 'onx' ? DEV2_ONX_BUBBLE_ID : undefined}
              />
            </>
          )}

          {actionMode === 'message' && (
            <>
              <FormControl fullWidth required>
                <InputLabel id="dev2-device-type-label">Product</InputLabel>
                <Select
                  labelId="dev2-device-type-label"
                  label="Product"
                  value={deviceType}
                  onChange={(e) => handleDeviceTypeChange(e.target.value)}
                >
                  {DEV2_DEVICE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Twilio From (member phone)"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                fullWidth
                required
                placeholder="+15551234567"
              />

              <TextField
                label="Twilio To (Dev2 inbound)"
                value={twilioTo}
                fullWidth
                disabled
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Latitude"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Longitude"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  fullWidth
                />
              </Stack>

              <FormControl fullWidth required>
                <InputLabel id="dev2-inbound-device-label">Device</InputLabel>
                <Select
                  labelId="dev2-inbound-device-label"
                  label="Device"
                  value={inboundDevice}
                  onChange={(e) => setInboundDevice(e.target.value)}
                >
                  {DEV2_INBOUND_DEVICE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Append to message Body (optional)"
                value={bodyAppend}
                onChange={(e) => setBodyAppend(e.target.value)}
                fullWidth
                placeholder="1 or 2 for reply-only"
              />

              {inboundMessageBody && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Message preview
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
                    <Typography
                      component="pre"
                      variant="body2"
                      sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}
                    >
                      {inboundMessageBody}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {inboundWebhookPreview && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Webhook preview
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, bgcolor: '#fafafa', maxHeight: 140, overflow: 'auto' }}>
                    <Typography
                      component="pre"
                      variant="caption"
                      sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'monospace' }}
                    >
                      {inboundWebhookPreview}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Stack>

        <Divider sx={{ mt: 3, mb: 2 }} />

        <Button
          variant="contained"
          color={actionMode === 'message' ? 'error' : 'primary'}
          onClick={runPrimaryAction}
          disabled={primaryBusy}
          fullWidth
          startIcon={primaryBusy ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {actionMode === 'schedule' ? 'Schedule device' : 'Send SOS/SMS'}
        </Button>
      </Paper>
    </Box>
  );
};

export default Dev2TestingPage;
